import AnnounceIcon from '@images/icons/announce-icon.svg?react'
import BankIcon from '@images/icons/bank_check_icon.svg?react'
import CheckIcon from '@images/icons/check-icon.svg?react'
import EthereumIcon from '@images/icons/ethereum_icon.svg?react'
import PhoneIcon from '@images/icons/phone_icon.svg?react'
import SepaIcon from '@images/icons/sepa_logo.svg?react'
import TermConditionsIcon from '@images/icons/terms_conditions_icon.svg?react'
import { formatCurrencyForDisplay } from '@utils'
import { paths, useDepositInitiateMutation, useDepositPreviewMutation, useMerchantQry } from 'mmfintech-backend-api'
import type { PaymentMethod } from 'mmfintech-backend-api/types'
import { GlobalContext, getCurrencySymbol, redirectToExternalUrl, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import ApplicationFeeButton from './ApplicationFeeButton'
import PaymentStep from './PaymentStep'

import './applicationFeeDetailsModal.scss'

const ApplicationFeeDetailsModal = () => {
  const { modalHide } = useContext(GlobalContext)

  const history = useHistory()

  const { merchant, merchantLoaded } = useMerchantQry()
  const { accountSetupFee } = merchant || {}

  const [previewDeposit, { error: previewError, isLoading: loadingPreview }] = useDepositPreviewMutation({
    fixedCacheKey: 'deposit-preview'
  })
  const [depositInitiate, { error: initiateError, isLoading: loadingInitiate }] = useDepositInitiateMutation({
    fixedCacheKey: 'deposit-initiate'
  })

  const displayCryptoInstructions = (paymentMethod: PaymentMethod, data: any) => {
    const { result, resultType } = data || {}

    if (resultType === 'REDIRECT_URL') {
      if (typeof result === 'string') {
        redirectToExternalUrl(result)
      } else {
        const { redirectUrl } = result || {}
        redirectToExternalUrl(redirectUrl)
      }
    } else {
      modalHide()
      history.push(paths.banking.deposit(), {
        paymentMethod,
        locationStep: paymentMethod === 'CRYPTO' ? 1 : 3,
        applicationFeeInitiateResponse: paymentMethod !== 'CRYPTO' ? data : undefined
      })
    }
  }

  const handleDepositPreview = async (paymentMethod: PaymentMethod) => {
    await previewDeposit({
      amount: accountSetupFee?.amount,
      currency: accountSetupFee?.currency,
      paymentMethod
    })
      .unwrap()
      .then(async () => {
        await depositInitiate({ amount: accountSetupFee?.amount, currency: accountSetupFee?.currency, paymentMethod })
          .unwrap()
          .then(response => {
            displayCryptoInstructions(paymentMethod, response)
          })
      })
  }

  const handleSelectPaymentOption = (method: PaymentMethod) => {
    if (merchantLoaded && !loadingPreview && !loadingInitiate) {
      if (accountSetupFee?.currency) {
        if (method === 'CRYPTO') {
          displayCryptoInstructions(method, null)
        } else {
          void handleDepositPreview(method)
        }
      }
    }
  }

  return (
    <div className='onboarding-application-fee-modal-wrapper'>
      <div className='onboarding-application-amount'>
        <div className='onboarding-application-amount-fee'>
          <span className='subtitle-label'>
            {tr('MERCHANTS.TRANSACTION.TYPE.ACCOUNT_SETUP_FEE', 'Application fee')}
          </span>
          <span className='subtitle-content'>
            {getCurrencySymbol(formatCurrencyForDisplay(accountSetupFee?.currency))} {accountSetupFee?.amount}
          </span>
        </div>
        <div className='onboarding-application-amount-total'>
          <span>{tr('FRONTEND.EXPENSES.TOTAL', 'Total')}</span>
          <span>
            {getCurrencySymbol(formatCurrencyForDisplay(accountSetupFee?.currency))} {accountSetupFee?.amount}
          </span>
        </div>
      </div>
      <div className='onboarding-application-content'>
        <div className='onboarding-application-content-announce'>
          <div className='onboarding-application-content-announce-icon'>
            <AnnounceIcon />
          </div>
          <span>
            {tr(
              'FRONTEND.ONBOARDING.APPLICATION_FEE_ANNOUNCE_TEXT',
              'The Kingdom Bank won’t charge your bank automatically. You’ll need to do this bit yourself to pay by bank transfer or crypto'
            )}
          </span>
        </div>

        <div className='onboarding-application-info'>
          <PaymentStep
            icon={<CheckIcon />}
            text={tr(
              'FRONTEND.APPLICATION_FEE_DETAILS_MODAL.CHOOSE_THE_PAYMENT_NETWORK_OF_YOUR_PREFERENCE_B_SEPA_B_OR_B_CRYPT',
              'Choose the payment network of your preference - <b>SEPA</b> or <b>Crypto</b>'
            )}
          />
          <PaymentStep
            icon={<BankIcon />}
            text={tr(
              'FRONTEND.APPLICATION_FEE.DETAILS.STEP_2',
              'Our bank/crypto transfer details will appear on the screen'
            )}
          />
          <PaymentStep
            icon={<PhoneIcon />}
            text={tr(
              'FRONTEND.APPLICATION_FEE.DETAILS.STEP_3',
              'Log into your crypto wallet or bank’s web service (you can also go to one of your bank’s branches) to transfer the funds'
            )}
          />
          <PaymentStep
            icon={<TermConditionsIcon />}
            text={tr(
              'FRONTEND.APPLICATION_FEE.DETAILS.STEP_4',
              'Don’t forget to add the <b>reference</b>. It will help us process your deposit faster'
            )}
          />
        </div>
      </div>

      <div className='onboarding-application-divider' />

      <div className='onboarding-application-button-wrapper'>
        {/*<ApplicationFeeButton
          icon={<SwiftIcon />}
          caption={tr('FRONTEND.APPLICATION_FEE.DETAILS.SWIFT_LABEL', 'SWIFT')}
          onClick={() => handleSelectPaymentOption('SWIFT_BANKWIRE')}
        />*/}
        <ApplicationFeeButton
          icon={<SepaIcon />}
          caption={tr('FRONTEND.DEPOSIT.PREVIEW.OPTIONS.SEPA', 'SEPA')}
          onClick={() => handleSelectPaymentOption('BANKWIRE')}
        />
        {merchant?.capabilities?.prospectCryptoExchangeEnabled && (
          <ApplicationFeeButton
            icon={<EthereumIcon />}
            caption={tr('FRONTEND.APPLICATION_FEE.DETAILS.CRYPTO_LABEL', 'CRYPTO')}
            onClick={() => handleSelectPaymentOption('CRYPTO')}
          />
        )}
      </div>

      <ErrorDisplay
        error={
          previewError || initiateError
            ? {
                ...previewError,
                ...initiateError,
                error:
                  'Contact your account manager for more information on how to complete the application fee payment.',
                errorKey: 'MERCHANTS.ERROR.PAYMENT_METHOD_UNAVAILABLE'
              }
            : null
        }
      />
    </div>
  )
}

export default ApplicationFeeDetailsModal
